/**
 * Consent state — deliberately free of React *and* of any Google API, so
 * the rules that actually matter legally (what a stored value means, what
 * an old value migrates to, which signals a choice maps to) are unit-
 * testable against nothing but document.cookie. See consent.test.js.
 *
 * Replaces react-cookie-consent's single boolean: that library stores one
 * `CookieConsent=true|false` cookie and has no concept of categories, so
 * a per-category choice can't be expressed in it at all.
 */

export const CONSENT_COOKIE = "tl-consent";

/**
 * react-cookie-consent's cookie, still on the devices of every visitor who
 * decided before this rewrite shipped. Read once and migrated (see
 * readConsent), never written.
 */
export const LEGACY_COOKIE = "CookieConsent";

/**
 * Bump when the CATEGORIES below change. A stored value from an older
 * version is treated as "no decision yet" and the banner asks again —
 * which is the point: consent given for two categories says nothing about
 * a third one that didn't exist when the visitor clicked.
 */
export const CONSENT_VERSION = 2;

/**
 * Everything beyond the technically necessary, one entry per banner
 * toggle, ordered from least to most intrusive.
 *
 * `maps` and `media` are site-side gates, not Consent Mode signals:
 * Google's v2 vocabulary has no equivalent for "may this page embed a
 * map", so nothing in consentSignals() reads them. They are enforced by
 * the components that would otherwise load the third party — see
 * useConsent.js.
 */
export const CATEGORIES = ["maps", "media", "analytics", "marketing"];

const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export const NO_CONSENT = Object.freeze(Object.fromEntries(CATEGORIES.map((k) => [k, false])));
export const FULL_CONSENT = Object.freeze(Object.fromEntries(CATEGORIES.map((k) => [k, true])));

/**
 * Dispatched on `window` after a choice is saved. The banner lives in its
 * own createRoot() outside the hydrated tree, so a component in #root
 * (the events map) has no shared provider to learn about a change
 * through — see the same reasoning on OPEN_CONSENT_EVENT.
 */
export const CONSENT_CHANGED_EVENT = "tl-consent-changed";

/**
 * Dispatched to reopen the banner (footer link, or the map's own
 * placeholder). Lives here rather than in CookieConsentRoot so a
 * component can ask for the settings dialog without importing the whole
 * banner — which would pull it into that route's bundle.
 */
export const OPEN_CONSENT_EVENT = "tl-open-consent";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [rawKey, ...rawValue] = part.split("=");
    if (rawKey.trim() === name) return decodeURIComponent(rawValue.join("="));
  }
  return null;
}

/** Only the known keys, coerced to real booleans — a stored `"true"` string
 * or a key we no longer use must not leak into the consent signals. */
function normalize(raw) {
  return CATEGORIES.reduce((acc, key) => ({ ...acc, [key]: raw?.[key] === true }), {});
}

/**
 * The visitor's stored decision, or `null` when they haven't made one yet.
 * `null` is what puts the banner on screen, so anything unparseable,
 * outdated or absent must land here rather than silently defaulting to
 * either extreme.
 */
export function readConsent() {
  const stored = readCookie(CONSENT_COOKIE);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.v === CONSENT_VERSION) return normalize(parsed);
    } catch {
      // Malformed value (hand-edited, truncated, or written by a version
      // that stored something else entirely) — fall through to the legacy
      // check and then to null, i.e. ask again.
    }
    return null;
  }

  // The old banner named "Google Analytics" and nothing else, so a `true`
  // there is informed consent for analytics ONLY. Carrying it over to
  // marketing as well would be reading an agreement the visitor never
  // saw — they get analytics kept and the marketing toggle stays off.
  const legacy = readCookie(LEGACY_COOKIE);
  if (legacy === "true") return { ...NO_CONSENT, analytics: true };
  if (legacy === "false") return { ...NO_CONSENT };
  return null;
}

export function writeConsent(choices) {
  const value = encodeURIComponent(JSON.stringify({ v: CONSENT_VERSION, ...normalize(choices) }));
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

/** Drops both our cookie and the legacy one — otherwise clearing the new
 * value would just re-migrate the old one on the next read. */
export function clearConsent() {
  for (const name of [CONSENT_COOKIE, LEGACY_COOKIE]) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  }
}

/**
 * Consent Mode v2 signals. Only `analytics` and `marketing` appear here:
 * the map and media categories gate whether an embed is loaded at all,
 * which Consent Mode has no signal for.
 *
 * The three ad_* signals move together because the banner offers one
 * "Marketing" toggle — splitting them in the UI would ask visitors to
 * distinguish "ad storage" from "ad user data", which is Google's
 * vocabulary, not anything a person can consent to meaningfully.
 */
export function consentSignals(choices) {
  const { analytics, marketing } = normalize(choices);
  const ads = marketing ? "granted" : "denied";
  return {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

/** True when `next` takes away something `previous` had allowed — the case
 * that needs cookies deleted and the page reloaded, since an already
 * loaded container doesn't unload its tags. */
export function withdrawsConsent(previous, next) {
  if (!previous) return false;
  return CATEGORIES.some((key) => previous[key] === true && next?.[key] !== true);
}
